import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import ErrorMessage from '../ui/ErrorMessage';
import { adminApi, AdminUsageStats, UsageEvent, UsageEventsResponse } from '../../services/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const UsageDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminUsageStats | null>(null);
  const [events, setEvents] = useState<UsageEvent[]>([]);
  const [eventsResponse, setEventsResponse] = useState<UsageEventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(30);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);

  const checkAdminAccess = async () => {
    try {
      // Try to call an admin API to check if user has admin access
      await adminApi.getSystemStats();
      setIsAdmin(true);
    } catch (err: any) {
      // Check if it's a 403 Forbidden error (not admin)
      if (err.response?.status === 403) {
        setIsAdmin(false);
      } else {
        // Other errors might be network issues, so we'll assume admin and let fetchUsageStats handle it
        setIsAdmin(true);
      }
    }
  };

  const fetchUsageStats = async () => {
    try {
      setLoading(true);
      
      const offset = (currentPage - 1) * pageSize;
      
      const [statsData, eventsData] = await Promise.all([
        adminApi.getUsageStats(selectedPeriod),
        adminApi.getUsageEvents({
          days: 7,
          limit: pageSize,
          offset: offset,
          user_id: selectedUser || undefined
        })
      ]);
      
      setStats(statsData);
      setEventsResponse(eventsData);
      setEvents(eventsData.events);
      setError(null);
    } catch (err: any) {
      // Check if it's an admin access error
      if (err.response?.status === 403) {
        setIsAdmin(false);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load usage data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeComponent = async () => {
      await checkAdminAccess();
    };
    initializeComponent();
  }, []);

  useEffect(() => {
    // Only fetch usage stats if we know the user is admin
    if (isAdmin === true) {
      setCurrentPage(1); // Reset to first page when filters change
    }
  }, [selectedPeriod, selectedUser, isAdmin]);

  useEffect(() => {
    // Only fetch usage stats if we know the user is admin
    if (isAdmin === true) {
      fetchUsageStats();
    }
  }, [currentPage, selectedPeriod, selectedUser, isAdmin]);

  // Show loading while checking admin status
  if (isAdmin === null) {
    return <LoadingSpinner />;
  }

  // Show error message if user is not admin
  if (isAdmin === false) {
    return <ErrorMessage />;
  }

  const formatCost = (cost: number) => `$${cost.toFixed(4)}`;
  const formatNumber = (num: number) => num.toLocaleString();
  const formatIsraeliTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      timeZone: 'Asia/Jerusalem',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Pagination calculations
  const totalPages = eventsResponse ? Math.ceil(eventsResponse.total_count / pageSize) : 0;
  const startItem = eventsResponse ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = eventsResponse ? Math.min(currentPage * pageSize, eventsResponse.total_count) : 0;

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="text-vibrant-orange text-xl mb-2">Error</div>
        <div className="text-surface-secondary">{error}</div>
      </div>
    </div>
  );
  if (!stats) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="text-surface-primary text-xl mb-2">No Data</div>
        <div className="text-surface-secondary">No usage data available</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-6 max-w-none w-full">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-bg-secondary">
          Usage Analytics
        </h2>
        <div className="flex gap-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="bg-white text-surface-primary px-4 py-2 rounded-lg border border-surface-muted/30 focus:border-vibrant-orange focus:ring-2 focus:ring-vibrant-orange/20 transition-all"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchUsageStats}
            className="bg-vibrant-orange text-white px-4 py-2 rounded-lg hover:bg-vibrant-orange/90 transition-all duration-200 text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <h3 className="text-sm font-medium text-surface-secondary mb-2">Total Users</h3>
          <p className="text-3xl font-bold text-surface-primary">{formatNumber(stats.total_users)}</p>
        </Card>
        
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <h3 className="text-sm font-medium text-surface-secondary mb-2">Active Users ({selectedPeriod}d)</h3>
          <p className="text-3xl font-bold text-surface-primary">{formatNumber(stats.active_users_30d)}</p>
        </Card>
        
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <h3 className="text-sm font-medium text-surface-secondary mb-2">Total Cost ({selectedPeriod}d)</h3>
          <p className="text-3xl font-bold text-vibrant-orange">{formatCost(stats.total_cost_30d)}</p>
        </Card>
        
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <h3 className="text-sm font-medium text-surface-secondary mb-2">Transcription Hours</h3>
          <p className="text-3xl font-bold text-muted-purple">{stats.transcription_hours_30d.toFixed(1)}h</p>
        </Card>
        
        <Card className="p-6 bg-white border border-surface-muted/20 shadow-lg">
          <h3 className="text-sm font-medium text-surface-secondary mb-2">AI Tokens</h3>
          <p className="text-3xl font-bold text-galaxy-pink">{formatNumber(stats.ai_tokens_30d)}</p>
        </Card>
      </div>

      {/* Cost Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-surface-primary mb-4">
            Cost by Category
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.cost_by_category).map(([category, cost]) => (
              <div key={category} className="flex justify-between items-center p-3 rounded-lg bg-surface-primary/30 hover:bg-surface-primary/50 transition-all">
                <span className="text-surface-primary capitalize font-medium">{category.replace('_', ' ')}</span>
                <span className="text-vibrant-orange font-bold">{formatCost(cost)}</span>
              </div>
            ))}
          </div>
        </Card>
        
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-surface-primary mb-4">
            Cost by Provider
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.cost_by_provider).map(([provider, cost]) => (
              <div key={provider} className="flex justify-between items-center p-3 rounded-lg bg-surface-primary/30 hover:bg-surface-primary/50 transition-all">
                <span className="text-surface-primary font-medium">{provider}</span>
                <span className="text-vibrant-orange font-bold">{formatCost(cost)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top Users */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-surface-primary mb-4">
          Top Users by Cost
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-muted/30">
                <th className="text-left py-3 text-surface-secondary font-semibold">User</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Total Cost</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Status</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_users.map((user: {
                user_id: number;
                email: string;
                total_cost: number;
                is_vip: boolean;
              }) => (
                <tr key={user.user_id} className="border-b border-surface-muted/20 hover:bg-surface-primary/20 transition-colors">
                  <td className="py-3 text-surface-primary font-medium">{user.email}</td>
                  <td className="py-3 text-vibrant-orange font-bold">{formatCost(user.total_cost)}</td>
                  <td className="py-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      user.is_vip 
                        ? 'bg-vibrant-orange/20 text-vibrant-orange border border-vibrant-orange/30' 
                        : 'bg-surface-muted/20 text-surface-primary border border-surface-muted/30'
                    }`}>
                      {user.is_vip ? 'VIP' : 'Regular'}
                    </span>
                  </td>
                  <td className="py-3">
                    <button
                      onClick={() => setSelectedUser(user.user_id)}
                      className="bg-white text-surface-primary border border-surface-muted/30 px-3 py-1.5 rounded-lg hover:bg-surface-primary/10 hover:text-vibrant-orange transition-all duration-200 text-sm font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent Events */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-surface-primary">
            Recent Usage Events
            {eventsResponse && (
              <span className="text-sm text-surface-secondary font-normal">
                ({formatNumber(eventsResponse.total_count)} total)
              </span>
            )}
          </h3>
          {selectedUser && (
            <button
              onClick={() => setSelectedUser(null)}
              className="bg-white text-surface-primary border border-surface-muted/30 px-3 py-1.5 rounded-lg hover:bg-surface-primary/10 hover:text-vibrant-orange transition-all duration-200 text-sm font-medium"
            >
              Clear Filter
            </button>
          )}
        </div>

        {/* Pagination Info */}
        {eventsResponse && eventsResponse.total_count > 0 && (
          <div className="flex justify-between items-center mb-4 text-sm text-surface-secondary">
            <div>
              Showing {startItem}-{endItem} of {formatNumber(eventsResponse.total_count)} events
            </div>
            <div className="flex items-center gap-2">
              <span>Page {currentPage} of {totalPages}</span>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-muted/30">
                <th className="text-left py-3 text-surface-secondary font-semibold">Time (IL)</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">User</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Event</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Category</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Cost</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Model</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Provider</th>
                <th className="text-left py-3 text-surface-secondary font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className="border-b border-surface-muted/20 hover:bg-surface-primary/20 transition-colors">
                  <td className="py-3 text-surface-primary">
                    {formatIsraeliTime(event.started_at)}
                  </td>
                  <td className="py-3 text-surface-primary">
                    {event.user_email || '-'}
                  </td>
                  <td className="py-3 text-surface-primary font-medium">
                    {event.event_type.replace(/_/g, ' ')}
                  </td>
                  <td className="py-3 text-surface-primary capitalize">
                    {event.category.replace(/_/g, ' ')}
                  </td>
                  <td className="py-3 text-vibrant-orange font-bold">
                    {formatCost(event.actual_cost_usd || event.estimated_cost_usd)}
                  </td>
                  <td className="py-3 text-muted-purple">
                    {event.model_used || '-'}
                  </td>
                  <td className="py-3 text-surface-primary">
                    {event.provider_used || '-'}
                  </td>
                  <td className="py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      event.error_message
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : event.completed_at
                        ? 'bg-muted-purple/20 text-muted-purple border border-muted-purple/30'
                        : 'bg-vibrant-orange/20 text-vibrant-orange border border-vibrant-orange/30'
                    }`}>
                      {event.error_message
                        ? 'Failed'
                        : event.completed_at
                        ? 'Complete'
                        : 'Processing'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {eventsResponse && totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  currentPage === 1
                    ? 'bg-surface-muted/20 text-surface-secondary cursor-not-allowed'
                    : 'bg-white text-surface-primary border border-surface-muted/30 hover:bg-surface-primary/10 hover:text-vibrant-orange'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {getVisiblePages().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="px-3 py-2 text-surface-secondary">...</span>
                    ) : (
                      <button
                        onClick={() => goToPage(page as number)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          currentPage === page
                            ? 'bg-vibrant-orange text-white'
                            : 'bg-white text-surface-primary border border-surface-muted/30 hover:bg-surface-primary/10 hover:text-vibrant-orange'
                        }`}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  currentPage === totalPages
                    ? 'bg-surface-muted/20 text-surface-secondary cursor-not-allowed'
                    : 'bg-white text-surface-primary border border-surface-muted/30 hover:bg-surface-primary/10 hover:text-vibrant-orange'
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            
            <div className="text-sm text-surface-secondary">
              {pageSize} events per page
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}; 